import type { TaskTemplateType } from '@econtractor/types';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UserNameItem } from 'modules/user';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import TaskTypeChip from '../../shared/TaskTypeChip';
import SolarIcon from 'components/icons/SolarIcon';
import RowTitle from '../../shared/RowTitle';

interface TaskDirTableRowProps {
    template: TaskTemplateType;
    hideExecutor?: boolean;
    onSelect?: (template: TaskTemplateType) => void;
    onEdit?: (template: TaskTemplateType) => void;
    onDelete?: (template: TaskTemplateType) => void;
}

const TaskDirTableRow = ({
    template,
    hideExecutor = false,
    onSelect,
    onEdit,
    onDelete,
}: TaskDirTableRowProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const open = onSelect || onEdit;
    const typeName = template.taskType
        ? tDirName(t, template.taskTypeI18nKey, template.taskTypeName)
        : '';

    return (
        <tr
            className={open ? 'cursor-pointer' : undefined}
            onClick={open ? () => (onSelect || onEdit)?.(template) : undefined}
        >
            <td className="name">
                <RowTitle name={template.taskName} description={template.taskDescription} />
            </td>
            <td className="category text-break">
                {template.categoryName ? template.categoryName : <span className="text-muted">—</span>}
            </td>
            <td className="type">
                <TaskTypeChip
                    name={typeName}
                    icon={template.taskTypeIcon}
                    colors={template.taslTypeJson}
                    fontSize="0.75rem"
                />
            </td>
            {hideExecutor ? null : (
                <td className="username">
                    {template.executorUser ? (
                        <UserNameItem user={template.executorUser} smallMode />
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </td>
            )}
            {onEdit || onDelete ? (
                <td className="action text-end" onClick={(e) => e.stopPropagation()}>
                    {onEdit ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-light-secondary p-1 py-0 me-1"
                            onClick={() => onEdit(template)}
                            aria-label={t('common:buttons.change')}
                        >
                            <SolarIcon name="pencil" className="fs-14" />
                        </button>
                    ) : null}
                    {onDelete ? (
                        <Button
                            type="button"
                            size="sm"
                            className="p-1 py-0"
                            variant="light-danger"
                            onClick={() => onDelete(template)}
                            aria-label={t('common:buttons.delete')}
                        >
                            <SolarIcon name="trash3" />
                        </Button>
                    ) : null}
                </td>
            ) : null}
        </tr>
    );
};

export default TaskDirTableRow;
